import React from "react";
import Input from "components/input";
import handleFormChange from "helpers/handle-form-change";
import { validatePhoneNumber } from "helpers/validations";
import PasswordInput from "components/password-input";
import ErrorMessage from "components/error-message";
import useSignupWithTags from "hooks/useSignupWithTags";
import { trackb2bRegistration } from "analytics";
import { useCustomer } from "hooks/useStore";
import Dropdown from "components/dropdown";
import clsx from "clsx/lite";

const RequiredFields = ({ requiredInputs, id, errors, formState, showLabel = false, layout }) =>
	requiredInputs.map(({ name, type = "text", placeholder = "", disabled = false, title, onChange = () => { } }) => {
		const isTypePassword = name === "password";
		if (isTypePassword && id) return null;
		const InputComponent = isTypePassword ? PasswordInput : Input;

		return (
			<InputComponent key={layout + name} error={errors[name]} name={name} value={formState[name]}
				type={type} disabled={disabled} label={showLabel && title} placeholder={placeholder || (!showLabel ? title : "")}
				required onChange={onChange} hideLabel={!showLabel}
			/>);
	});

const Form = ({ className, showLabel = false, dropDownOptions = false, buttonClass, buttonText, layout, tag = "MOD PRO" }) => {
	const { email, firstName, lastName, id } = useCustomer(["email", "firstName", "lastName", "id"]);

	const initialFormState = {
		firstName: firstName || "", lastName: lastName || "", email: email || "", password: "", phoneNumber: "",
		acceptsMarketing: true, company: "", companyAddress: "", jobTitle: "", companySize: "", role: "",
	};

	const track = () => {
		trackb2bRegistration({
			value: `Step 1 of 1 / form fill completion`,
		});
	};

	const { formState, setFormState, errors, setErrors, loading, formSubmit } =
		useSignupWithTags({
			initialState: initialFormState, tags: [tag, "MOD PRO SUBMIT"], track, scrollTop: true
		});

	const requiredInputs = [
		{ name: "firstName", title: "First Name", disabled: firstName },
		{ name: "lastName", title: "Last Name", disabled: lastName },
		{ name: "email", title: "Email", disabled: email },
		{ name: "password", title: "Password", type: "password" },
		{
			name: "phoneNumber", title: "Phone Number", type: "tel",
			placeholder: !showLabel ? "Phone Number" : "+1 (123) 456-7890",
			onChange: (e) => validatePhoneNumber(e, setErrors)
		},
	];


	return (
		<>
			{errors?.form && <ErrorMessage message={errors?.form} className="justify-center w-4/5" />}
			<form className={clsx("flex mt-8 flex-col gap-2 text-black", className)}
				onSubmit={formSubmit} onChange={(e) => handleFormChange(e, setFormState)}
			>
				<RequiredFields id={id} errors={errors} formState={formState}
					requiredInputs={requiredInputs} showLabel={showLabel} layout={layout} />
				<Input error={errors?.company} name="company" label={showLabel && "Company Name"}
					value={formState.company} placeholder={!showLabel ? "Company Name" : "ex. MOD LIGHTING"}
				/>
				{!dropDownOptions &&
					<>
						<Input error={errors?.address} name="companyAddress" label={showLabel && "Company Address"}
							value={formState.address} placeholder={!showLabel && "Company Address"}
						/>
						<Input error={errors?.jobTitle} name="jobTitle" label={showLabel && "Job Title"}
							value={formState.jobTitle} placeholder={!showLabel && "Job Title"}
						/>
					</>}
				{dropDownOptions &&
					<>
						<div className="relative border border-beige border-opacity-30 p-1 shadow bg-white">
							{showLabel &&
								<label htmlFor="country" className="absolute text-xs top-0 left-0 px-3 py-2 text-darkGray">
									Company size
								</label>}
							<Dropdown
								Preview={() =>
									<p className="pr-2">
										{formState.companySize ? formState.companySize : <span className="opacity-40">Company Size</span>}
									</p>}
								className="focus-within:!outline-0" activeClass="!border-none"
								value={formState.companySize} placeholder="Select" id="companySize" name="companySize"
							>
								{["1-10", "10-50", "50-100", "100+"].map(opt => <option key={opt} value={opt}>{opt}</option>)}
							</Dropdown>
						</div>
						<div className="relative border border-beige border-opacity-30 p-1 shadow bg-white">
							{showLabel &&
								<label htmlFor="country" className="absolute text-xs top-0 left-0 px-3 py-2 text-darkGray">
									Your Role
								</label>}
							<Dropdown
								Preview={() =>
									<p className="pr-2">
										{formState.role ? formState.role : <span className="opacity-40">Your Role</span>}
									</p>
								}
								className="focus-within:!outline-0" activeClass="!border-none"
								value={formState.role} id="role" name="role"
							>
								{["Founder / CEO", "Director", "Manager", "Other"].map(opt => <option key={opt} value={opt}>{opt}</option>)}
							</Dropdown>
						</div>
					</>
				}
				<div className={clsx("flex flex-col items-center justify-center mb-4", buttonClass)}>
					<button disabled={loading} type="submit" className="py-5 btn-primary w-full">
						{buttonText || "Join Free"}
					</button>
				</div>
			</form>
		</>
	);
};

export default Form;